import axios from "axios";
import { isAndroid, isIOS } from "react-device-detect";

import { BASE_URL } from "../../actions";

export const APP_DEEP_LINK_SCHEME = "biosynciq";
export const ANDROID_PACKAGE_NAME = "com.biosynciq";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.biosynciq";
export const APP_STORE_URL =
  "https://apps.apple.com/us/app/biosync-iq/id6799152053";

export const getEnrollmentPlatform = () => {
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  return "web";
};

export const isMobileDevice = () => isIOS || isAndroid;

const buildDeepLinkQuery = ({ enrollmentToken, token } = {}) => {
  if (enrollmentToken) {
    return `enrollment_token=${encodeURIComponent(enrollmentToken)}`;
  }
  if (token) {
    return `token=${encodeURIComponent(token)}`;
  }
  return "";
};

export const buildPlayStoreUrl = ({ enrollmentToken, token } = {}) => {
  const referrer = buildDeepLinkQuery({ enrollmentToken, token });
  if (!referrer) return PLAY_STORE_URL;
  return `${PLAY_STORE_URL}&referrer=${encodeURIComponent(referrer)}`;
};

// App Store cannot accept a JWT referrer — that causes
// "Cannot connect to iTunes Store". iOS gets a clean listing URL.
export const buildAppStoreUrl = () => APP_STORE_URL;

// execCommand helper used when Clipboard API is unavailable/blocked.
const copyTextWithExecCommand = (text) => {
  if (!text || typeof document === "undefined") return false;

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.contentEditable = "true";
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.width = "1px";
  textarea.style.height = "1px";
  textarea.style.padding = "0";
  textarea.style.border = "none";
  textarea.style.outline = "none";
  textarea.style.boxShadow = "none";
  textarea.style.background = "transparent";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);

  const range = document.createRange();
  range.selectNodeContents(textarea);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
  textarea.setSelectionRange(0, text.length);
  textarea.focus();

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  }

  selection.removeAllRanges();
  document.body.removeChild(textarea);
  return copied;
};

export const copyTextToClipboard = async (text) => {
  if (!text || typeof document === "undefined") return false;

  // Prefer Clipboard API on user-gesture paths (Allow / Download taps).
  // Running execCommand first steals focus from the button on iOS Safari and
  // can burn the transient activation before writeText runs.
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to execCommand when Clipboard API is blocked.
  }

  return copyTextWithExecCommand(text);
};

export const copyIosLandingUrlToClipboard = async () => {
  if (!isIOS || typeof window === "undefined") return false;
  return copyTextToClipboard(window.location.href);
};

// Try the app deeplink first (Safari + Chrome on iOS).
//
// App installed → iOS/Chrome shows "Open this page in 'BioSyncIQ'?":
//   - while sheet is up, focus is lost → do not show our App Store popup
//   - user taps Open → page hides → done
//   - user taps Cancel → focus returns without hide → do NOT show our popup
//
// App not installed (no OS open sheet, page stays focused) → show our popup.
export const openAppOrFallbackToStore = ({
  appUrl,
  onStoreFallback,
  onSkipStorePrompt,
} = {}) => {
  if (typeof window === "undefined") return () => {};

  let settled = false;
  let appOpened = false;
  let lostFocusAfterDeeplink = false;
  let timer = null;
  const startedAt = Date.now();
  const INITIAL_WAIT_MS = 2500;
  const RETRY_MS = 600;
  const MAX_WAIT_MS = 10000;

  const clearTimer = () => {
    if (timer != null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  const cleanup = () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("focus", onFocus);
  };

  const finishWithoutStorePrompt = () => {
    if (settled) return;
    settled = true;
    clearTimer();
    cleanup();
    if (typeof onSkipStorePrompt === "function") {
      onSkipStorePrompt();
    }
  };

  const markAppOpened = () => {
    appOpened = true;
    finishWithoutStorePrompt();
  };

  const showStorePrompt = () => {
    if (settled || appOpened) return;
    settled = true;
    clearTimer();
    cleanup();
    if (typeof onStoreFallback === "function") {
      onStoreFallback();
    }
  };

  const scheduleCheck = (delay) => {
    clearTimer();
    timer = window.setTimeout(checkWhetherToShowStore, delay);
  };

  const isPageHidden = () =>
    document.hidden || document.visibilityState === "hidden";

  const checkWhetherToShowStore = () => {
    if (settled || appOpened) {
      cleanup();
      return;
    }

    // User tapped Open on the system sheet — app is launching.
    if (isPageHidden()) {
      markAppOpened();
      return;
    }

    // System open sheet (or browser alert) is up — keep waiting, no our popup.
    if (!document.hasFocus()) {
      lostFocusAfterDeeplink = true;
      if (Date.now() - startedAt < MAX_WAIT_MS) {
        scheduleCheck(RETRY_MS);
        return;
      }
      // Still unfocused after max wait — do not show store popup.
      finishWithoutStorePrompt();
      return;
    }

    // Focus came back after an OS sheet/alert. That is Cancel (or dismiss),
    // not "app missing" — never show our App Store popup in this case.
    if (lostFocusAfterDeeplink) {
      finishWithoutStorePrompt();
      return;
    }

    // Never lost focus after deeplink → app almost certainly not installed.
    showStorePrompt();
  };

  const onVisibilityChange = () => {
    if (isPageHidden()) {
      markAppOpened();
      return;
    }
    // Becoming visible again in Chrome/Safari — re-check.
    if (!settled && !appOpened) {
      scheduleCheck(RETRY_MS);
    }
  };

  const onPageHide = () => {
    markAppOpened();
  };

  const onPageShow = () => {
    if (!settled && !appOpened) {
      scheduleCheck(RETRY_MS);
    }
  };

  const onBlur = () => {
    lostFocusAfterDeeplink = true;
    if (!settled && !appOpened) {
      scheduleCheck(RETRY_MS);
    }
  };

  const onFocus = () => {
    if (!settled && !appOpened) {
      scheduleCheck(RETRY_MS);
    }
  };

  if (!appUrl) {
    showStorePrompt();
    return () => {
      settled = true;
      clearTimer();
    };
  }

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("blur", onBlur);
  window.addEventListener("focus", onFocus);

  scheduleCheck(INITIAL_WAIT_MS);
  window.location.href = appUrl;

  return () => {
    settled = true;
    clearTimer();
    cleanup();
  };
};

const buildCustomSchemeUrl = (query) =>
  `${APP_DEEP_LINK_SCHEME}://practice_detail${query ? `?${query}` : ""}`;

// On Android, an intent URL opens the app when installed and falls through to
// Play Store with the same token as an install referrer when it is not.
const buildAndroidIntentUrl = ({ enrollmentToken, token } = {}) => {
  const query = buildDeepLinkQuery({ enrollmentToken, token });
  const playStoreUrl = buildPlayStoreUrl({ enrollmentToken, token });

  return (
    `intent://practice_detail${query ? `?${query}` : ""}` +
    `#Intent;scheme=${APP_DEEP_LINK_SCHEME};package=${ANDROID_PACKAGE_NAME};` +
    (query ? `S.market_referrer=${encodeURIComponent(query)};` : "") +
    `S.browser_fallback_url=${encodeURIComponent(playStoreUrl)};end`
  );
};

const buildAppOpenUrl = ({ enrollmentToken, token } = {}) => {
  const query = buildDeepLinkQuery({ enrollmentToken, token });
  if (isAndroid) {
    return buildAndroidIntentUrl({ enrollmentToken, token });
  }
  return buildCustomSchemeUrl(query);
};

export const buildEnrollmentAppUrl = (enrollmentToken) =>
  buildAppOpenUrl({ enrollmentToken });

export const buildPracticeTokenAppUrl = (token) =>
  buildAppOpenUrl({ token });

export const MISSING_ENROLLMENT_MESSAGE =
  "Please scan the practice QR code first, then use this form to sign up.";

export const INVALID_ENROLLMENT_LINK_MESSAGE =
  "This QR code is no longer valid. Please scan a new QR code to continue.";

export const INVALID_LINK_MESSAGE =
  "This link is no longer valid. Please request a new link to continue.";

const getLinkErrorMessage = (error, fallbackMessage) => {
  const apiMessage =
    error.response?.data?.msg ||
    error.response?.data?.message ||
    error.response?.data?.error;

  if (
    error.response?.status === 404 ||
    error.response?.status === 410 ||
    apiMessage === "Invalid or expired enrollment link" ||
    apiMessage === "Invalid link" ||
    apiMessage === "This link has expired" ||
    error.message === "Invalid or expired enrollment link" ||
    error.message === "Invalid link"
  ) {
    return fallbackMessage;
  }

  if (apiMessage) {
    return apiMessage;
  }

  return error.message || fallbackMessage;
};

const resolveToken = async (token, { source, fallbackMessage } = {}) => {
  const platform = getEnrollmentPlatform();
  const params = new URLSearchParams({ platform });
  if (source) {
    params.set("source", source);
  }

  try {
    const response = await axios.get(
      `${BASE_URL}/resolve-token/${encodeURIComponent(token)}?${params.toString()}`,
    );

    if (!response?.data?.ok) {
      throw new Error(response?.data?.msg || fallbackMessage);
    }

    return {
      agencyId: response?.data?.agency_id,
      agencyName: response?.data?.agency_name,
      patientId: response?.data?.patient_id,
      purpose: response?.data?.purpose,
    };
  } catch (error) {
    throw new Error(getLinkErrorMessage(error, fallbackMessage));
  }
};

export const resolveEnrollmentToken = async (token) =>
  resolveToken(token, {
    source: "qr",
    fallbackMessage: INVALID_ENROLLMENT_LINK_MESSAGE,
  });

export const resolvePracticeToken = async (token) =>
  resolveToken(token, {
    source: "qr",
    fallbackMessage: INVALID_LINK_MESSAGE,
  });

export const buildEnrollmentQuery = ({ enrollmentToken, agencyName }) => {
  const params = new URLSearchParams({ enrollment_token: enrollmentToken });
  if (agencyName) {
    params.set("agency_name", agencyName);
  }
  return params.toString();
};

const isBlank = (value) => !value || !String(value).trim();
const isInvalidAgencyId = (agencyId) =>
  isBlank(agencyId) || agencyId === "null" || agencyId === "undefined";

export const isMissingEnrollmentContext = ({
  enrollmentToken,
  agencyId,
  agencyName,
}) => {
  if (enrollmentToken) return false;

  return isInvalidAgencyId(agencyId) && isBlank(agencyName);
};
